import { Message } from "./types"
import { ChatMessage } from "./ChatMessage"
import { precedingUserQuestion } from "@/lib/precedingUserQuestion"
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation"

interface ChatMessagesProps {
  messages: Message[]
  sessionId: string
  /** Display name for the signed-in user (shown above user bubbles). */
  userName?: string
  /** In-session image thumbnails, keyed by message id (live preview only). */
  messagePreviews?: Record<string, string[]>
  onFeedbackSubmit: (
    messageContent: string,
    question: string,
    feedbackType: "positive" | "negative",
    comment: string
  ) => Promise<void>
  /**
   * Factory for a click-time download-URL resolver, keyed by an artifact's
   * (runId, name). Returns null when a fresh URL can't be minted (e.g. local
   * build / not signed in), in which case ArtifactCard keeps its plain link.
   */
  onResolveDownloadUrl?: (runId?: string, name?: string) => (() => Promise<string>) | null
}

/**
 * The conversation thread. Renders messages inside the auto-following
 * `Conversation` viewport (which owns scroll + the jump-to-latest button), so no
 * manual scroll ref is needed.
 *
 * @param messages - ordered messages to render.
 * @param sessionId - active session id (passed through to each message).
 * @param onFeedbackSubmit - feedback handler, curried with the message content
 *   and with the user turn that produced it. The question is resolved here
 *   because this is where the ordered thread and the index are both in hand; the
 *   handler above only forwards it to the POST.
 * @param onResolveDownloadUrl - click-time download-URL resolver factory.
 */
export function ChatMessages({
  messages,
  sessionId,
  userName,
  messagePreviews,
  onFeedbackSubmit,
  onResolveDownloadUrl,
}: ChatMessagesProps) {
  return (
    <Conversation conversationKey={sessionId}>
      <ConversationContent>
        {messages.map((message, index) => (
          <ChatMessage
            key={message.id}
            message={message}
            sessionId={sessionId}
            userName={userName}
            imagePreviews={messagePreviews?.[message.id]}
            onFeedbackSubmit={async (feedbackType, comment) => {
              await onFeedbackSubmit(
                message.content,
                precedingUserQuestion(messages, index),
                feedbackType,
                comment
              )
            }}
            onResolveDownloadUrl={onResolveDownloadUrl}
          />
        ))}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  )
}
